import { useState } from 'react'
import type { ValueT } from '../api'
import { recaptureSource } from '../api'
import { ClauseSnippet } from './ClauseSnippet'
import { ReportError } from './ReportError'

export function ValueCell({
  v,
  unit,
  label,
  overrideContext,
  fieldKey,
}: {
  v: ValueT
  unit?: string
  label?: string  // human-readable control name, used in the copy text
  overrideContext?: {
    target_type: 'lep' | 'dcp'
    lga_slug: string
    zone?: string
    housing_form?: string
  }
  fieldKey?: string
}) {
  const [showSource, setShowSource] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const isAffirmativeNone = v.value === null && v.confidence === 'green'
  const isUnavailable = v.value === null && v.confidence === 'red'
  const display = isAffirmativeNone
    ? 'Not affected'
    : isUnavailable
    ? 'Verify manually'
    : formatValue(v.value)
  const valueWithUnit = `${display}${unit && !isAffirmativeNone && !isUnavailable ? ` ${unit}` : ''}`
  // Copy makes no sense for null values ("Verify manually" /
  // "Not affected") — there's nothing to copy. Same for the override-
  // submission "Report error" flow.
  const hasRealValue = v.value !== null && v.value !== undefined && v.value !== ''
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center">
        <ConfidenceDot c={v.confidence} />
        <span className={isAffirmativeNone ? 'text-massing-ink-mid' : isUnavailable ? 'text-massing-maroon' : ''}>
          {valueWithUnit}
        </span>
        {v.override_applied && (
          <span className="ml-2 text-[10px] uppercase tracking-wide bg-massing text-white px-1.5 py-0.5 rounded">
            verified
          </span>
        )}
      </div>
      {v.citation && (
        <div className="text-[11px] text-massing-ink-light">{v.citation}</div>
      )}
      {v.notes && v.notes.length > 0 && (
        <div className="text-[11px] text-massing-ink-light italic">{v.notes.join('; ')}</div>
      )}
      <div className="flex gap-3 text-[11px]">
        <button
          type="button"
          onClick={() => setShowSource((s) => !s)}
          className="text-massing hover:underline"
          title="Where this value came from"
        >
          {showSource ? 'Hide source' : 'Source'}
        </button>
        {hasRealValue && (
          <CopyClauseButton text={buildCopyText(label, valueWithUnit, v.citation)} />
        )}
        {overrideContext && fieldKey && hasRealValue && (
          <button type="button" onClick={() => setShowReport(true)} className="text-massing-ink-light hover:text-massing-maroon hover:underline">
            Report error
          </button>
        )}
      </div>
      {showSource && (
        <SourcePanel
          v={v}
          recaptureCtx={
            overrideContext && v.citation
              ? {
                  source_type: overrideContext.target_type,
                  lga_slug: overrideContext.lga_slug,
                  citation: v.citation,
                  field_key: fieldKey ?? null,
                }
              : null
          }
        />
      )}
      {showReport && overrideContext && fieldKey && (
        <ReportError
          onClose={() => setShowReport(false)}
          context={{ ...overrideContext, field_key: fieldKey, current_value: v.value }}
        />
      )}
    </div>
  )
}

// Full provenance panel for a single value. Designed so a planner can
// audit every number end-to-end without leaving the report: where the
// value came from, when it was parsed / pulled, by what, the verbatim
// extract, and a link to the authoritative source where we have one.
// The intent is to make every claim falsifiable in one click.
function SourcePanel({
  v,
  recaptureCtx,
}: {
  v: ValueT
  recaptureCtx: {
    source_type: 'lep' | 'dcp'
    lga_slug: string
    citation: string
    field_key: string | null
  } | null
}) {
  const [recaptured, setRecaptured] = useState<string | null>(null)
  const [recaptureNote, setRecaptureNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function tryRecapture() {
    if (!recaptureCtx) return
    setBusy(true)
    setRecaptureNote(null)
    try {
      const r = await recaptureSource(recaptureCtx)
      if (r.source_text) {
        setRecaptured(r.source_text)
      } else {
        setRecaptureNote(r.reason ?? 'Could not recapture the source text.')
      }
    } catch (e) {
      setRecaptureNote(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const rows: Array<{ label: string; node: React.ReactNode }> = []
  if (v.source) rows.push({ label: 'Source', node: v.source_url ? (
    <a href={v.source_url} target="_blank" rel="noopener noreferrer" className="text-massing hover:underline">
      {v.source}
    </a>
  ) : v.source })
  if (v.citation) rows.push({ label: 'Citation', node: v.citation })
  if (v.layer) rows.push({ label: 'Layer', node: v.layer })
  if (v.layer_version) rows.push({ label: 'Layer version', node: v.layer_version })
  if (v.parsed_at) rows.push({ label: 'Last reviewed', node: formatTimestamp(v.parsed_at) })
  rows.push({ label: 'Confidence', node: <ConfidenceLabel c={v.confidence} /> })
  if (v.override_applied) rows.push({ label: 'Override', node: 'A manual override was applied to this value.' })
  return (
    <div className="mt-2 rounded-md bg-massing-paper-deep/80 border border-massing-rule px-3 py-2.5 text-massing-ink">
      <div className="brand-eyebrow text-massing-ink-light mb-2">Provenance</div>
      <table className="w-full text-[12px]">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="align-top">
              <th className="text-left brand-eyebrow text-massing-ink-light pr-3 py-0.5 w-32 whitespace-nowrap font-normal">{r.label}</th>
              <td className="py-0.5">{r.node}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 pt-3 border-t border-massing-rule-soft">
        <div className="brand-eyebrow text-massing-ink-light mb-1.5">Source text</div>
        {(v.source_text || recaptured) ? (
          <>
            <ClauseSnippet
              text={recaptured ?? v.source_text ?? ''}
              source={v.source}
              citation={v.citation}
            />
            {recaptured && (
              <div className="mt-1.5 text-[11px] text-massing-ink-light italic">
                Recaptured from the source instrument just now.
              </div>
            )}
          </>
        ) : (
          <div className="text-[12px] text-massing-ink-light italic leading-snug">
            No verbatim extract recorded for this control.{' '}
            {v.citation
              ? <>See {v.source ? <>{v.source}, </> : null}{v.citation} for the source clause.</>
              : <>Verify against the source document before relying on this value.</>}
            {recaptureCtx && (
              <div className="mt-2 not-italic">
                <button
                  type="button"
                  onClick={tryRecapture}
                  disabled={busy}
                  className="text-[11px] uppercase tracking-wide px-2 py-1 border border-massing-rule rounded-md text-massing-ink hover:border-massing-highlight transition-colors disabled:opacity-50"
                >
                  {busy ? 'Recapturing…' : 'Try to recapture from source'}
                </button>
                {recaptureNote && (
                  <div className="text-[11px] text-massing-maroon italic mt-1.5">
                    {recaptureNote}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ConfidenceDot({ c }: { c: ValueT['confidence'] }) {
  const cls =
    c === 'green' ? 'bg-flag-green' :
    c === 'amber' ? 'bg-flag-amber' :
    c === 'red'   ? 'bg-flag-red' :
                    'bg-massing-ink-light/40'
  const title =
    c === 'green' ? 'High confidence — verified or deterministic source.' :
    c === 'amber' ? 'Medium confidence — read from the source document; verify before relying on for a DA.' :
    c === 'red'   ? 'Low confidence — source missing, conflicting, or unverified.' :
                    'Out of scope — not applicable to this lot.'
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${cls}`}
      title={title}
      aria-label={`confidence: ${c}`}
    />
  )
}

function ConfidenceLabel({ c }: { c: ValueT['confidence'] }) {
  const text =
    c === 'green' ? 'High — verified or deterministic.' :
    c === 'amber' ? 'Medium — read from the source document. Verify before relying on for a DA.' :
    c === 'red'   ? 'Low — source missing, conflicting, or unverified.' :
                    'Out of scope.'
  return (
    <span className="inline-flex items-center gap-2">
      <ConfidenceDot c={c} />
      {text}
    </span>
  )
}

function formatTimestamp(iso: string): string {
  // Show YYYY-MM-DD HH:mm in local time so the planner knows when the
  // value was parsed without having to mentally translate from UTC.
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
  } catch {
    return iso
  }
}

function buildCopyText(label: string | undefined, valueWithUnit: string, citation: string | null): string {
  // Build a planner-friendly one-liner like:
  //   "Front setback: min 9 m — DCP Part 4A.2 (front)"
  //   "FSR (resolved): 0.384 — clause 4.4(2A) sliding scale row 800-1000"
  //   "Bushfire prone land: Vegetation Buffer — Bush Fire Prone Land layer"
  // The clause / citation is always included so a planner can paste the
  // result straight into an LEP/DCP search bar.
  const head = label ? `${label}: ${valueWithUnit}` : valueWithUnit
  // Trim the verbose ":1 = X" or "row X-Y: formula = N" tails to the
  // clause/part header alone, keeping the copy text tight.
  const cite = citation ? extractClauseLabel(citation) : null
  return cite ? `${head} — ${cite}` : head
}

function extractClauseLabel(citation: string): string {
  // Keep up to the first ":" or " — " or "(...)" — that's the clause /
  // chapter identifier the planner cares about.
  const m = citation.match(/^[^:——]+/)
  return (m ? m[0] : citation).trim()
}

function CopyClauseButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      title={`Copy "${text}"`}
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="text-massing hover:underline"
    >
      {copied ? '✓ copied' : 'Copy'}
    </button>
  )
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
    return String(v)
  }
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    const parts: string[] = []
    // Known shape: per-storey setbacks with min / ground / first_floor /
    // upper / rear_upper / etc. Capture every numeric subfield so a
    // {min: 1.5, upper: 2.5} setback prints "min 1.5 m, upper 2.5 m"
    // instead of falling through to "[object Object]".
    const labels: Record<string, string> = {
      min: 'min',
      ground: 'ground',
      first_floor: 'first floor',
      upper: 'upper',
      rear: 'rear',
      rear_upper: 'rear upper',
      side: 'side',
      side_upper: 'side upper',
      front: 'front',
    }
    for (const [k, v2] of Object.entries(o)) {
      if (typeof v2 === 'number' && labels[k]) parts.push(`${labels[k]} ${v2} m`)
    }
    if (typeof o.midwinter_hours_to_pos === 'number') {
      parts.push(`${o.midwinter_hours_to_pos} h midwinter sun to POS`)
    }
    if (parts.length) {
      // When there's both numeric setbacks AND a rule string, the rule
      // explains how the numbers were derived (e.g. "averaging of three
      // closest residential buildings"). Append it parenthetically.
      if (typeof o.rule === 'string' && o.rule.trim()) {
        return parts.join(', ') + ` (${o.rule.trim()})`
      }
      return parts.join(', ')
    }
    // Only a rule string — typical for "averaging method" front setbacks
    // where the LEP doesn't give a fixed metre value. Show the rule as
    // the value rather than falling through to "[object Object]".
    if (typeof o.rule === 'string' && o.rule.trim()) return o.rule.trim()
    // Last resort: stringify any remaining text/number leaves, skipping
    // internal metadata keys.
    const fallback = Object.entries(o)
      .filter(([k, val]) => !k.startsWith('_') && k !== 'source_text' && (typeof val === 'number' || typeof val === 'string'))
      .map(([k, val]) => `${k.replace(/_/g, ' ')} ${val}`)
    if (fallback.length) return fallback.join(', ')
    return '—'   // give up rather than print [object Object]
  }
  return String(v)
}
